import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { waHref, ASSETS } from "@/lib/contact";

const CHAPTERS = [
  {
    n: "01",
    title: "Guincho para Carros",
    desc: "Pane, batida ou pneu furado — retiramos seu carro com plataforma segura em minutos.",
    img: ASSETS.car,
  },
  {
    n: "02",
    title: "Guincho para Motos",
    desc: "Transporte protegido com amarração profissional. Sem riscos de arranhões ou quedas.",
    img: ASSETS.moto,
  },
  {
    n: "03",
    title: "Utilitários e SUVs",
    desc: "Suportamos EcoSport, Hilux, S10 e carga leve com plataforma reforçada.",
    img: ASSETS.suv,
  },
  {
    n: "04",
    title: "Reboque Particular",
    desc: "Mudança de garagem, entrega de veículo em oficina ou remoção urbana. Discreto e rápido.",
    img: ASSETS.sedan,
  },
];

export default function Services() {
  const [active, setActive] = useState(0);

  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="relative bg-brand-black py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        {/* Section header */}
        <div className="flex items-center gap-4 border-b border-chrome/15 pb-8">
          <span className="font-mono text-xs tracking-[0.3em] text-brand-yellow">
            [ SEÇÃO 01 · SERVIÇOS ]
          </span>
          <span className="h-px flex-1 bg-chrome/20" />
        </div>

        {/* Chapters + fixed spotlight */}
        <div className="mt-10 grid grid-cols-12 gap-x-8 gap-y-4">
          <div className="col-span-12 lg:col-span-7">
            <ul className="flex flex-col">
              {CHAPTERS.map((c, i) => (
                <li
                  key={c.n}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  className={`group border-t border-chrome/15 py-6 transition-colors sm:py-8 ${
                    active === i ? "text-white" : "text-neutral-400"
                  } ${i === CHAPTERS.length - 1 ? "border-b" : ""}`}
                  data-testid={`chapter-${c.n}`}
                >
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-4"
                  >
                    <div className="flex items-baseline gap-5 sm:gap-8">
                      <span className="font-mono text-xs tracking-widest text-brand-yellow sm:text-sm">
                        {c.n}
                      </span>
                      <div>
                        <h3 className="font-display text-2xl uppercase leading-none tracking-tight transition-colors group-hover:text-brand-yellow sm:text-4xl">
                          {c.title}
                        </h3>
                        <p className="mt-2 max-w-md font-body text-sm text-neutral-500 sm:text-[15px]">
                          {c.desc}
                        </p>
                        {/* Mobile inline image */}
                        <div className="mt-4 aspect-[16/10] w-full overflow-hidden bg-neutral-900 lg:hidden">
                          <img
                            src={c.img}
                            alt={c.title}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                    <ArrowUpRight
                      className={`h-8 w-8 shrink-0 transition-all sm:h-10 sm:w-10 ${
                        active === i
                          ? "text-brand-yellow translate-x-1 -translate-y-1"
                          : "text-chrome/40"
                      }`}
                      strokeWidth={1.5}
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Spotlight image (desktop) */}
          <div className="hidden lg:col-span-5 lg:block">
            <div className="sticky top-24">
              <div className="relative aspect-[4/5] w-full overflow-hidden border border-chrome/20">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active}
                    src={CHAPTERS[active].img}
                    alt={CHAPTERS[active].title}
                    initial={{ clipPath: "inset(0 100% 0 0)" }}
                    animate={{ clipPath: "inset(0 0% 0 0)" }}
                    exit={{ clipPath: "inset(0 0 0 100%)" }}
                    transition={{ duration: 0.65, ease: [0.77, 0, 0.175, 1] }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                {/* frame corners */}
                <span className="pointer-events-none absolute left-3 top-3 h-4 w-4 border-l-2 border-t-2 border-brand-yellow" />
                <span className="pointer-events-none absolute right-3 top-3 h-4 w-4 border-r-2 border-t-2 border-brand-yellow" />
                <span className="pointer-events-none absolute left-3 bottom-3 h-4 w-4 border-l-2 border-b-2 border-brand-yellow" />
                <span className="pointer-events-none absolute right-3 bottom-3 h-4 w-4 border-r-2 border-b-2 border-brand-yellow" />
                {/* caption */}
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 flex items-end justify-between p-4">
                  <span className="font-mono text-[10px] tracking-[0.3em] text-white/90">
                    ATENDIMENTO REAL · {CHAPTERS[active].n}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-brand-yellow">
                    BELÉM / PA
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
